import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {addGrid, getNoOfGridItems} from '@Components/base-grid';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {TextToImageGridErrorState} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {Text, TextSize, TextType} from '@Components/text';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {getAssetUrl} from '@Utils/s3.util';
import StockPanelError from '@Panels/stock-panel/components/stock-panel-error';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import {getUserGeneratedImages} from '@Libraries/pmw-stock-media-library';
import {getGridItemForAIGeneratedImageV2} from '@Panels/stock-panel/stock-panel.library';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './text-to-image-grid.module.scss';

const INFOGRAPHIC_IMAGE_URL = getAssetUrl(`texttoimage/no-image-placeholder.${getCompatibleImageFileExtension('webp')}`);

const GRID_ITEMS_DEFAULT_HEIGHT = 160;

export function TextToImageGrid({isLoading = false, isHidden = false, ...props}: TextToImageGridProps): ReactElement {
  const [infographicLoaded, setInfographicLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const numberOfGridItems = useAppSelector((state) => {
    return getNoOfGridItems(state, props.gridId);
  });
  const isInitialState = numberOfGridItems <= 0 && !isLoading;

  const getPreSearchInfographic = (): ReactElement => {
    return (
      <div className={`${isHidden ? '_hidden' : ''} flex-v-row flex-center flex-1`}>
        <img
          className={!infographicLoaded ? styles.infographicImageNotVisible : props.infographicClasses ?? styles.infographicImage}
          onLoad={() => {
            setInfographicLoaded(true);
          }}
          src={INFOGRAPHIC_IMAGE_URL}
          loading="lazy"
          alt={window.i18next.t('pmwjs_ai_generated_image')}
        />
        {!infographicLoaded ? <SkeletonBrick className={props.infographicClasses ?? styles.infographicImage} /> : null}
        <Text
          type={TextType.BODY}
          className={`content-body text-center ${props.infographicTextClasses}`}
          size={TextSize.XSMALL}
          val={window.i18next.t('pmwjs_text_to_image_infographic_text')}
        />
      </div>
    );
  };

  const getItems = async (params: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    const offset = Object.keys(params.offsets).reduce((agg, item) => {
      return agg + params.offsets[item];
    }, 0);

    const items = await getUserGeneratedImages(offset, params.limit);

    const gridItems = items.map(getGridItemForAIGeneratedImageV2);

    return {response: gridItems, offsetsData: {DEFAULT_SOURCE: gridItems.length}};
  };

  const getGrid = (): ReactElement => {
    return (
      <>
        <LazyMasonryGrid
          {...props}
          getItems={getItems}
          key={props.gridId}
          gridId={props.gridId}
          defaultHeight={GRID_ITEMS_DEFAULT_HEIGHT}
          className={`${isHidden ? '_hidden' : ''} ${props.gridClasses} ${isInitialState ? 'spacing-p-0' : ''}`}
          clearGridOnUnmount
        />
        {isInitialState ? getPreSearchInfographic() : null}
      </>
    );
  };

  const getErrorMessage = (): string => {
    switch (props.errorState) {
      case TextToImageGridErrorState.GENERIC:
        return window.i18next.t('pmwjs_refresh_on_error');
      case TextToImageGridErrorState.FLAGGED:
        return window.i18next.t('pmwjs_search_prompt_violation_message');
      case TextToImageGridErrorState.UNSUPPORTED_LANUAGE:
        return window.i18next.t('pmwjs_search_prompt_unsupported_language_message');
      case TextToImageGridErrorState.NONE:
      default:
        throw new Error(`Unhandled error state: ${props.errorState}`);
    }
  };

  const getErrorState = (): ReactElement => {
    return (
      <StockPanelError
        className={`flex-1 ${props.errorState === TextToImageGridErrorState.FLAGGED ? 'spacing-p-r-5 spacing-p-l-5' : ''}`}
        onRefresh={
          props.errorState === TextToImageGridErrorState.GENERIC && props.onRetry
            ? (): void => {
                if (props.onRetry) {
                  props.onRetry();
                }
              }
            : undefined
        }
        text={getErrorMessage()}
      />
    );
  };

  const getGridContainer = (): ReactElement => {
    if (props.errorState !== TextToImageGridErrorState.NONE) {
      return getErrorState();
    }
    return getGrid();
  };

  useEffect(() => {
    dispatch(
      addGrid({
        id: props.gridId,
        gridSelectionGroupId: props.gridSelectionGroupId,
        items: [],
      })
    );
  }, []);

  return getGridContainer();
}
